/**
 * Wire shapes. Every one mirrors a record on the host, named in its comment, in camelCase:
 *
 * - reader: `Rigo.Platform.Contracts.Support` (packages/dotnet/Rigo.Platform.Contracts/Support)
 * - console: `Rigo.Platform.Support.Host/Kb/KbConsoleContracts.cs`, `Kb/KbAuthoringService.cs`,
 *   `Api/KbConsoleEndpoints.cs`, `Api/ConsoleEndpoints.cs`, `Api/MeEndpoints.cs`
 * - sign-in: `Bff/BffEndpoints.cs`
 *
 * Enumerations are the host's persisted codes (DRAFT, ANONYMOUS, "Support Admin"), not the
 * numbers RigoHR's feat-rigohr-kb branch used. A shape change at integration is an edit here and
 * in `endpoints.ts`, and nowhere else.
 */

/** RFC 9457 problem details, which every refusal on /api and /bff is. */
export type Problem = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  /**
   * Only on `/bff/user`'s 401 (`{ authenticated: false, localLogin }`): true when the host offers
   * its own password sign-in, which is Development only (ADR-0059). Otherwise staff sign in
   * through RigoHR.
   */
  localLogin?: boolean;
};

/** Who may read a document: an ascending ladder (`KbReaderRoles`). Lowest first. */
export const ReaderRole = {
  Anonymous: "ANONYMOUS",
  CustomerEmployee: "CUSTOMER_EMPLOYEE",
  CustomerAdmin: "CUSTOMER_ADMIN",
  InternalSupport: "INTERNAL_SUPPORT",
} as const;

export type ReaderRole = (typeof ReaderRole)[keyof typeof ReaderRole];

export const READER_ROLE_LABELS: Record<string, string> = {
  [ReaderRole.Anonymous]: "Public",
  [ReaderRole.CustomerEmployee]: "Customer employee",
  [ReaderRole.CustomerAdmin]: "Customer admin",
  [ReaderRole.InternalSupport]: "Internal support",
};

/** Publishing state (`KbArticleStatus`). */
export const ArticleStatus = {
  Draft: "DRAFT",
  InReview: "IN_REVIEW",
  Published: "PUBLISHED",
} as const;

export type ArticleStatus = (typeof ArticleStatus)[keyof typeof ArticleStatus];

export const ARTICLE_STATUS_LABELS: Record<string, string> = {
  [ArticleStatus.Draft]: "Draft",
  [ArticleStatus.InReview]: "In review",
  [ArticleStatus.Published]: "Published",
};

/**
 * A publishing-workflow move. The branch sent one `status` call with a number; the host has one
 * route per move: `POST /api/kb/articles/{id}/{submit|send-back|publish|unpublish}`.
 */
export type ArticleTransition = "submit" | "send-back" | "publish" | "unpublish";

/** The five console roles (`SupportRoles`). One staff record: `support.agent` (ADR-0046 decision 7). */
export const SupportRole = {
  SupportAdmin: "Support Admin",
  SupportAgent: "Support Agent",
  ContentPublisher: "Content Publisher",
  ContentReviewer: "Content Reviewer",
  ContentAuthor: "Content Author",
} as const;

export type SupportRole = (typeof SupportRole)[keyof typeof SupportRole];

/** The roles the documentation workspace's members screen manages. */
export const WORKSPACE_ROLES: SupportRole[] = [
  SupportRole.SupportAdmin,
  SupportRole.ContentPublisher,
  SupportRole.ContentReviewer,
  SupportRole.ContentAuthor,
];

/** A staff member's state (`AgentStatus`). */
export const AgentStatus = {
  Active: "ACTIVE",
  Invited: "INVITED",
  Inactive: "INACTIVE",
} as const;

export type AgentStatus = (typeof AgentStatus)[keyof typeof AgentStatus];

export const AGENT_STATUS_LABELS: Record<string, string> = {
  [AgentStatus.Active]: "Active",
  [AgentStatus.Invited]: "Invited",
  [AgentStatus.Inactive]: "Inactive",
};

// ---- sign-in (/bff) ----

/** `BffUser`. */
export type BffUser = {
  authenticated: boolean;
  subject: string | null;
  name: string | null;
  email: string | null;
  organisationId: string | null;
  role: string | null;
};

/** `LoginResponse`. */
export type LoginOutcome = {
  mfaRequired: boolean;
  userId: string;
  role: string | null;
};

// ---- reader-facing shapes (/kb) ----

/** `KbArticleCard`. */
export type ArticleCard = {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  sectionName: string;
  categorySlug: string;
  categoryName: string;
};

/** `KbCategoryCard`. */
export type CategoryCard = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  articleCount?: number;
};

/** `KbUpdate`. */
export type DocsUpdate = {
  slug: string;
  title: string;
  publishedOn: string;
};

/** `KbHome`. */
export type DocsHome = {
  workspaceName: string;
  categories: CategoryCard[];
  recommended: ArticleCard[];
  latestUpdates: DocsUpdate[];
};

/** `KbCategoryPage`. */
export type CategoryPage = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  articles: ArticleCard[];
};

/** `KbNavItem`. Listed-but-locked items carry `restricted`, so a reader knows to sign in. */
export type NavItem = {
  slug: string;
  title: string;
  restricted: boolean;
  children: NavItem[];
};

/** `KbNavSection`. */
export type NavSection = {
  name: string;
  articles: NavItem[];
};

/** `KbNavCategory`. */
export type NavCategory = {
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  sections: NavSection[];
};

/** `KbTocEntry`. */
export type TocEntry = {
  id: string;
  text: string;
  level: number;
};

/** `KbArticleResponse`. Also what the console's preview answers. */
export type Article = {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  /** Server-sanitized markup (KbHtmlSanitizer). Still rendered through sanitizeArticleHtml. */
  contentHtml: string;
  categorySlug: string;
  categoryName: string;
  sectionName: string;
  publishedAt: string | null;
  updatedAt: string | null;
  showLastUpdated: boolean;
  toc?: TocEntry[];
  previous?: ArticleCard | null;
  next?: ArticleCard | null;
};

/** `KbSearchHit`. `excerpt` is `ts_headline` output: `<mark>` around matches in unescaped text — render with Excerpt, never as HTML. */
export type SearchHit = {
  slug: string;
  title: string;
  categoryName: string;
  excerpt: string;
  rank?: number;
};

/** `KbReadEventRequest`. */
export type ReadEventRequest = {
  articleSlug: string;
  userRef: string | null;
  anonId: string | null;
  device: string | null;
};

/** `KbReadEventResult`. Null when the read folded into a recent one. */
export type ReadEventResult = { readEventId: number | null };

// ---- console shapes (/api/kb) ----

/** `KbMe`. */
export type WorkspaceMe = {
  agentId: string | null;
  displayName: string;
  email: string | null;
  role: string | null;
  workspaceRole: string | null;
  isAdmin: boolean;
  canWrite: boolean;
  canReview: boolean;
  canPublish: boolean;
  canViewAnalytics: boolean;
  capabilities: string[];
};

/** `KbAdminArticleRow`. */
export type AdminArticleRow = {
  id: string;
  slug: string;
  title: string;
  sectionName: string;
  categoryName: string;
  status: ArticleStatus;
  /** When the host's row does not say (`ArticleRow`), the article's own rung or ANONYMOUS. */
  effectiveMinRole: string;
  inheritsMinRole: boolean;
  /** Present on `ArticleRow`, so an inherited rung can be read off the structure tree. */
  sectionId?: string;
  authorName: string | null;
  lastUpdatedOn: string | null;
};

/** The editor's view of one article (`ArticleEditor`, flattened by `endpoints.ts`). */
export type AdminArticle = {
  id: string;
  sectionId: string;
  parentArticleId: string | null;
  slug: string;
  title: string;
  summary: string | null;
  contentJson: string | null;
  contentHtml: string | null;
  status: ArticleStatus;
  minRole: string | null;
  sortOrder: number;
  authorName: string | null;
  lastUpdatedOn: string | null;
};

/** `ArticleDraft`: what the editor saves. */
export type ArticleSave = {
  sectionId: string;
  parentArticleId: string | null;
  slug: string;
  title: string;
  summary: string | null;
  contentJson: string | null;
  contentHtml: string | null;
  minRole: string | null;
  sortOrder: number;
};

/** `StructureArticleRow`. */
export type StructureArticle = {
  id: string;
  title: string;
  sortOrder: number;
  status?: ArticleStatus;
  parentArticleId?: string | null;
};

/** `SectionRow`. */
export type StructureSection = {
  id: string;
  name: string;
  sortOrder: number;
  articleCount?: number;
  articles: StructureArticle[];
};

/** `CategoryRow`: the structure screen's tree, and the categories screen's list. */
export type StructureCategory = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  minRole: string;
  seoIndexable: boolean;
  sortOrder: number;
  articleCount: number;
  sections: StructureSection[];
};

/** `KbStructureSave`. */
export type StructureSave = {
  categories: {
    id: string;
    name: string | null;
    sortOrder: number;
    sections: {
      id: string;
      name: string | null;
      sortOrder: number;
      articles: { id: string; title: string | null; sortOrder: number }[];
    }[];
  }[];
};

/** `CategoryBody`. */
export type CategorySave = {
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  minRole: string;
  seoIndexable: boolean;
  sortOrder: number;
};

/** `SectionBody` (create) and `SectionUpdateBody` (update). */
export type SectionSave = {
  categoryId: string;
  name: string;
  sortOrder: number;
};

/** `AgentRow` from `GET /api/agents`. */
export type Agent = {
  id: string;
  fullName: string;
  workEmail: string;
  role: string;
  status: string;
  isLinked: boolean;
  openTicketCount: number;
};

/** `UpsertAgent`. */
export type AgentSave = {
  fullName: string;
  workEmail: string;
  role: string;
  status: string | null;
};

/** `KbSettingsResponse`; the save body (`KbSettingsBody`) is the same fields. */
export type WorkspaceSettings = {
  workspaceName: string;
  supportEmail: string | null;
  defaultMinRole: string;
  locale: string;
  requireReview: boolean;
  allowComments: boolean;
  showLastUpdated: boolean;
  publicBaseUrl?: string | null;
  maxMediaBytes?: number;
  maxMediaPerUpload?: number;
  allowedExtensions?: string[];
  readEventDedupeMinutes?: number;
  searchResultLimit?: number;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
};

/** `KbMediaItem`. */
export type MediaItem = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  categoryId: string | null;
  categoryName: string | null;
  createdOn: string;
  /** Titles of the live articles that use it (`KbMediaItem`); empty when only a count is sent. */
  usedIn: string[];
  /** How many (`MediaRow.UsedInCount`, or the length of `usedIn`). */
  usedInCount: number;
  assetPath: string;
};

/** `KbStagedMedia`. */
export type StagedMedia = {
  stagingKey: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
};

/** `KbMediaUploadResult`. */
export type MediaUploadResult = {
  staged: StagedMedia[];
  rejected: string[];
};

/** `KbArticleStats`. */
export type ArticleStats = {
  articleId: string;
  slug: string;
  title: string;
  sectionName: string;
  views: number;
  readers: number;
  averageSeconds: number | null;
  trendPercent: number | null;
};

/** `KbReaderVisit`. */
export type ReaderVisit = {
  articleId: string;
  articleTitle: string;
  userId: string | null;
  tenantId: string | null;
  readerRole: string;
  occurredOn: string;
  device: string | null;
  country: string | null;
  durationSec: number | null;
};

/** `KbTimeBucket`. */
export type TimeBucket = {
  bucketStart: string;
  views: number;
  readers: number;
};

/** `KbArticleAnalytics`. */
export type ArticleAnalytics = {
  summary: ArticleStats;
  timeline: TimeBucket[];
  recentReaders: ReaderVisit[];
};

/** `KbReaderLookup`. */
export type ReaderLookup = {
  tenantId: string | null;
  userId: string | null;
  totalReads: number;
  visits: ReaderVisit[];
};
